<template>
  <div class="manufac-box mt-4">
    <v-row>
      <v-col
        cols="12"
        md="4">
        <v-select
          v-model="form.manufacCountry"
          :items="countries"
          :menu-props="{ offsetY: true }"
          label="ผลิตที่"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details />
      </v-col>
      <v-col
        cols="12"
        md="4">
        <v-text-field
          v-model.number="form.sellPrice"
          type="number"
          dense
          label="ราคาขาย (บาท)"
          :error-messages="sellPriceError"
          :disabled="!hasManufacCountry"
          outlined
          onwheel="this.blur()"
          @keypress="$keypressNumber($event)"
          @wheel="$event.target.blur()" />
      </v-col>
      <v-col
        cols="12"
        md="4">
        <v-text-field
          v-model.number="form.manufacCost"
          type="number"
          dense
          :label="`ค่าผลิต (${currencyLabel})`"
          :disabled="!hasManufacCountry"
          :error-messages="manufacCostError"
          outlined
          onwheel="this.blur()"
          @keypress="$keypressNumber($event)"
          @wheel="$event.target.blur()" />
      </v-col>
    </v-row>
    <v-row
      v-for="(c, i) in form.colors"
      :key="i">
      <v-col cols="12">
        <p class="bold text-uppercase">
          สี {{ c.color }}
        </p>
        <v-divider />
      </v-col>
      <v-col cols="12">
        <span class="bold">รายการอะไหล่</span>
        <v-row>
          <v-col
            v-for="(material, index) in c.materials"
            :key="`material-field-${index}`"
            cols="12">
            <v-row>
              <v-col cols="6">
                <div class="d-flex">
                  <p class="cost-calculate-label">
                    ชื่อ {{ material.name }} =
                    {{
                      material.currency === 'thb'
                        ? material.unitPrice
                        : `(${material.unitPrice} x ${mappedCurrency[material.currency]})`
                    }} x {{ material.amount }} =
                  </p>
                  <p class="cost-calculate-result ml-1">
                    {{ totalCostCal(
                      material.unitPrice * mappedCurrency[material.currency],
                      material.amount
                    ) | showFullPriceFormat()
                    }} บาท
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <span class="bold">รายการผ้า</span>
        <v-row>
          <v-col
            v-for="(fabric, index) in c.fabrics"
            :key="`fabric-field-${index}`"
            cols="12">
            <v-row>
              <v-col cols="6">
                <div class="d-flex">
                  <p class="cost-calculate-label">
                    รหัส {{ fabric.fabricNo }} ({{ fabric.hangId }}) =
                    {{
                      fabric.currency === 'thb'
                        ? fabric.unitPrice
                        : `(${fabric.unitPrice} x ${mappedCurrency[fabric.currency]})`
                    }} x {{ fabric.amount }} =
                  </p>
                  <p class="cost-calculate-result ml-1">
                    {{ totalCostCal(
                      fabric.unitPrice * mappedCurrency[fabric.currency],
                      fabric.amount
                    ) | showFullPriceFormat()
                    }} บาท
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="6">
        <div class="d-flex">
          <p class="cost-calculate-label">
            ราคาต้นทุน
            {{ isThailand ? manufacCost : `(${manufacCost} x ${currencyMultiple})` }} + {{ materialsPrice }} + {{ fabricsPrice }} =
          </p>
          <p class="cost-calculate-result ml-1">
            {{ totalCostPrice | showFullPriceFormat() }} บาท
          </p>
        </div>
        <div class="d-flex">
          <p class="cost-calculate-label">
            ราคาไม่รวม VAT = ({{ (form.sellPrice || 0) | showFullPriceFormat() }} / {{ 1.07 }}) =
          </p>
          <p class="cost-calculate-result ml-1">
            {{ (priceWithoutVat || 0) | showFullPriceFormat() }} บาท
          </p>
        </div>
        <div class="d-flex">
          <p class="cost-calculate-label">
            Margin = ({{ (sellPrice / 1.07) | showFullPriceFormat() }} / {{ totalCostPrice | showFullPriceFormat() }}) =
          </p>
          <p class="cost-calculate-result ml-1">
            {{ margin | showFullPriceFormat() }}
          </p>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PrototypeCurrency from '@/assets/json/PrototypeCurrency.json'

export default {
  props: {
    value: {
      type: Object,
      default: () => {}
    },
    margin: {
      type: Number,
      default: 0
    },
    isThailand: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      replacement: [],
      countries: PrototypeCurrency
    }
  },
  computed: {
    form: {
      get () {
        return this.value
      },
      set (newVal) {
        this.$emit('input', newVal)
      }
    },
    clothesLength () {
      return this.form.clothesLength || 0
    },
    clothesCostPerMetr () {
      return this.form.clothesCostPerMetr || 0
    },
    manufacCost () {
      return this.form.manufacCost || 0
    },
    sellPrice () {
      return this.form.sellPrice || 0
    },
    mappedCurrency () {
      return this.countries.reduce((acc, c) => ({ ...acc, [c.currency]: c.multiple }), {})
    },
    country () {
      return this.countries.find((c) => c.value === this.form.manufacCountry)
    },
    currencyLabel () {
      return this.country?.currency?.toUpperCase() || this.form.manufacCountry
    },
    currencyMultiple () {
      return this.country?.multiple || 1
    },
    materialsPrice () {
      const allMaterials = this.form?.colors?.reduce((arr, c) => [...arr, ...c.materials], []) || []

      return allMaterials.reduce((acc, cur) => acc + (cur.amount * cur.unitPrice * this.mappedCurrency[cur.currency]), 0) || 0
    },
    fabricsPrice () {
      const allFabrics = this.form?.colors?.reduce((arr, c) => [...arr, ...c.fabrics], []) || []

      return allFabrics.reduce((acc, cur) => acc + (cur.amount * cur.unitPrice * this.mappedCurrency[cur.currency]), 0) || 0
    },
    totalCostPrice () {
      if (this.isThailand) {
        return this.manufacCost + this.materialsPrice + this.fabricsPrice || 0
      }

      return (this.manufacCost * this.currencyMultiple) + this.materialsPrice + this.fabricsPrice || 0
    },
    priceWithoutVat () {
      return this.form?.sellPrice ? this.form.sellPrice / 1.07 : 0
    },
    clothesCostPerMetrError () {
      return this.isValid(this.fabricsPrice) ? '' : 'กรุณากรอกราคาผ้า'
    },
    manufacCostError () {
      return this.isValid(this.form.manufacCost) ? '' : 'กรุณากรอกค่าผลิต'
    },
    sellPriceError () {
      return this.isValid(this.form.sellPrice) ? '' : 'กรุณากรอกราคาขาย'
    },
    manufacCountryError () {
      const hasForm = !!this.form.sellPrice || !!this.form.manufacCost || !!this.materialsPrice || !!this.fabricsPrice

      return !this.form.manufacCountry && hasForm ? 'กรุณาเลือกประเทศที่ผลิต' : ''
    },
    hasManufacCountry () {
      return !!this.form.manufacCountry
    }
  },
  watch: {
    replacement: {
      handler (value) {
        this.form.replacement = value
        this.$emit('input', this.form)
      },
      deep: true
    }
  },
  methods: {
    totalCostCal (price, amount) {
      return price * amount || null
    },
    isEmpty (value, alert) {
      return !value ? alert : ''
    },
    clearCountry () {
      this.form.manufacCountry = null
    },
    isValid (data) {
      return !!this.form.manufacCountry && (!!data || data === 0)
    },
    addMoreReplacementField () {
      this.form.replacement.push({ name: null, value: null })
    },
    removeReplacementField (index) {
      this.form.replacement.splice(index, 1)
    },
    replacementCompute (replacement) {
      return replacement?.reduce((total, current) => total + current, 0) || 0
    }
  }
}
</script>

<style>
.field-label {
  font-weight: bold;
  margin: 0px;
}
.cost-calculate-result {
  font-weight: bold;
}
.manufac-box {
  width: 100%;
}
.clear-country-btn {
  margin: 22px 8px 0;
}
.bold {
  font-weight: bold;
}
.capitalize {
  text-transform: capitalize;
}
</style>
